import { useEffect } from 'react';
import notifee, { EventType } from '@notifee/react-native';
import { getMessaging, onMessage } from '@react-native-firebase/messaging';
import { getApp, getApps, initializeApp } from '@react-native-firebase/app';
import { config } from './firebase';
import store from '../redux/Store';
import { addNotification } from '../redux/notificationsSlice';
import ChatwootService from '../services/ChatwootService';
import { ensureDefaultNotificationChannel } from '../services/requestNotificationsOnUserGesture';
import { isLoginPromptNotificationContent } from '../utils/Background';
import { handleGettingStartedNotificationPress } from '../navigation/gettingStartedPushNav';
import { handleDeepLinkNotificationPress } from '../navigation/deepLinks/deepLinkRouter';

const handleChatwootForegroundMessage = (remoteMessage: any) => {
  if (!ChatwootService.isChatwootAgentMessageData(remoteMessage?.data)) {
    return false;
  }

  const unreadCount = Number(remoteMessage.data.unread_count || 0);
  ChatwootService.setUnreadCount(unreadCount);
  return true;
};

const NotificationHandler = () => {
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let notifeeUnsubscribe: (() => void) | undefined;

    const initializeFirebaseMessaging = async () => {
      // Ensure Firebase is initialized before using messaging
      if (getApps().length === 0) {
        console.log('Initializing Firebase in NotificationHandler...');
        await initializeApp(config);
      }
      return getMessaging(getApp());
    };

    const initializeNotifications = async () => {
      try {
        // Do NOT request permission here — wait for notification bell / Chatwoot tap.
        await ensureDefaultNotificationChannel();

        // Listen for notifee events (foreground notification tap, etc)
        notifeeUnsubscribe = notifee.onForegroundEvent(async event => {
          const { type, detail } = event;
          if (type === EventType.DISMISSED) {
            console.log('Notification dismissed', detail.notification);
          } else if (type === EventType.PRESS) {
            console.log('Notification pressed', detail.notification);
            // Handle navigation or other actions based on notification data
            const data = detail.notification?.data;
            console.log('Notification data:', data);
            const opened = await ChatwootService.openChatFromPush(data);
            if (opened) {
              return;
            }
            const handledDeepLink = await handleDeepLinkNotificationPress(data);
            if (handledDeepLink) {
              return;
            }
            const handledGettingStarted =
              await handleGettingStartedNotificationPress(data);
            if (handledGettingStarted) {
              return;
            }
            const pressTitle = (detail.notification?.title || '').trim();
            const pressBody = (detail.notification?.body || '').trim();
            if (!pressTitle && !pressBody) {
              return;
            }
            store.dispatch(
              addNotification({
                id: detail.notification?.id,
                title: pressTitle || 'Notification',
                body: pressBody,
                source: 'push',
                data: data as Record<string, any>,
              }),
            );
          }
        });

        const initialNotification = await notifee.getInitialNotification();
        if (initialNotification?.notification?.data) {
          const data = initialNotification.notification.data;
          const opened = await ChatwootService.openChatFromPush(data);
          if (opened) {
            return;
          }
          const openedDeepLink = await handleDeepLinkNotificationPress(data);
          if (!openedDeepLink) {
            await handleGettingStartedNotificationPress(data);
          }
        }

        // Get Firebase messaging instance with proper initialization
        const messaging = await initializeFirebaseMessaging();

        // Handle FCM messages when app is in foreground
        unsubscribe = onMessage(messaging, async remoteMessage => {
          console.log('Received FCM message:', remoteMessage);
          if (handleChatwootForegroundMessage(remoteMessage)) {
            return;
          }

          const title = (
            remoteMessage.notification?.title ||
            (remoteMessage.data as Record<string, any>)?.title ||
            ''
          ).trim();
          const body = (
            remoteMessage.notification?.body ||
            (remoteMessage.data as Record<string, any>)?.body ||
            (remoteMessage.data as Record<string, any>)?.message ||
            ''
          ).trim();

          if (!title && !body) {
            console.log('Dropping FCM with no displayable content:', remoteMessage.messageId);
            return;
          }

          if (isLoginPromptNotificationContent({ title, body })) {
            return;
          }

          store.dispatch(
            addNotification({
              id: remoteMessage.messageId || undefined,
              title: title || 'Notification',
              body,
              source: 'push',
              data: remoteMessage.data as Record<string, any>,
            }),
          );
          await notifee.displayNotification({
            id: remoteMessage.messageId || undefined,
            title: title || 'Notification',
            body,
            android: {
              channelId: 'default',
              importance: 4,
              vibrationPattern: [0, 250, 250, 250],
              pressAction: { id: 'default', launchActivity: 'default' },
            },
            data: remoteMessage.data,
          });
        });
      } catch (error) {
        console.error('Error initializing notifications:', error);
      }
    };

    initializeNotifications();

    return () => {
      if (unsubscribe) unsubscribe();
      if (notifeeUnsubscribe) notifeeUnsubscribe();
    };
  }, []);

  return null;
};

export default NotificationHandler;
